import { useState, type JSX } from 'react';
import { 
  Paper, 
  Text, 
  Group, 
  Stack, 
  Badge, 
  Skeleton, 
  Avatar, 
  UnstyledButton 
} from '@mantine/core';
import { ConversationThread } from '../../models/conversation';

interface ReflectionThreadListProps {
  threads: ConversationThread[];
  isLoading: boolean;
  selectedThreadId: string | null;
  onSelectThread: (threadId: string) => void;
}

/**
 * Displays a list of reflection guide threads for the client
 */
export function ReflectionThreadList({
  threads,
  isLoading,
  selectedThreadId,
  onSelectThread
}: ReflectionThreadListProps): JSX.Element {
  
  // Format timestamp for display
  const formatTimestamp = (date?: Date): string => {
    if (!date) return '';
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // If date is today, show time
    if (date >= today) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // If date is yesterday, show "Yesterday"
    if (date >= yesterday) {
      return 'Yesterday';
    }
    
    // Otherwise show date
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };
  
  // Handle thread selection
  const handleSelectThread = (threadId: string) => {
    onSelectThread(threadId);
  };
  
  // Loading state with skeletons
  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        {[1, 2, 3].map((i) => (
          <Paper key={i} withBorder p="md" mb="md">
            <Group justify="space-between" mb="xs">
              <Skeleton height={20} width="60%" />
              <Skeleton height={16} width={50} radius="xl" />
            </Group>
            <Skeleton height={16} width="80%" mb="xs" />
            <Group justify="space-between">
              <Skeleton height={14} width="40%" />
              <Skeleton height={14} width={60} />
            </Group>
          </Paper>
        ))}
      </Paper>
    );
  }
  
  // Empty state
  if (threads.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Text ta="center" size="lg" c="dimmed">
          No reflection sessions yet
        </Text>
        <Text ta="center" size="sm" c="dimmed" mt="sm">
          Start a new session to begin reflecting on your thoughts and feelings.
        </Text>
      </Paper>
    );
  }
  
  // List of threads
  return (
    <Stack gap="md">
      {threads.map((thread) => (
        <UnstyledButton
          key={thread.id}
          onClick={() => handleSelectThread(thread.id)}
          style={{ width: '100%', textAlign: 'left' }}
        >
          <Paper 
            withBorder 
            p="md" 
            bg={selectedThreadId === thread.id ? 'var(--mantine-color-blue-0)' : undefined}
            style={{ 
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            <Group justify="space-between" mb="xs">
              <Text fw={600} size="md" lineClamp={1}>
                {thread.topic}
              </Text>
            </Group>
            
            <Text size="sm" c="dimmed" lineClamp={2} mb="xs">
              {thread.lastMessage || 'No messages yet'}
            </Text>
            
            <Group justify="space-between">
              <Group gap="xs">
                <Avatar 
                  size="xs" 
                  radius="xl" 
                  color="blue"
                >
                  RG
                </Avatar>
                <Text size="xs" c="dimmed">
                  Reflection Guide
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {formatTimestamp(thread.lastMessageSentAt)}
              </Text>
            </Group>
          </Paper>
        </UnstyledButton>
      ))}
    </Stack>
  );
}